import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LogOut, Trash2, UserPlus, Users, X } from "lucide-react";

import { AppStateContext } from "@/state/AppStateContext";
import { deleteGroupConversation, fetchWithAuth, leaveGroupConversation, removeGroupParticipant } from "@/api/api";
import { Conversation, GroupParticipant } from "@/api/models";
import InviteParticipantsModal from "@/components/GroupConversation/InviteParticipantsModal";
import { mergeGroupParticipants, replaceConversationParticipants, syncGroupConversationState } from "@/components/GroupConversation/groupConversationState";
import { clearAgent, clearChatTransientState, clearCustomGPT } from "@/utils/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

interface GroupParticipantsProps {
  conversation: Conversation;
}

const AVATAR_COLORS = [
  'bg-primary', 'bg-secondary', 'bg-accent',
  'bg-info', 'bg-success', 'bg-warning',
];

const GroupParticipants = ({ conversation }: GroupParticipantsProps) => {
  const appStateContext = useContext(AppStateContext);
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [removingParticipantUserId, setRemovingParticipantUserId] = useState<string | null>(null);
  const [photos, setPhotos] = useState<Record<string, string | null>>({});

  const participants: GroupParticipant[] = conversation.participants || [];
  const currentUser = appStateContext?.state.currentUser;
  const currentChat = appStateContext?.state.currentChat;
  const isOwner = conversation.ownerId === currentUser?.id;
  const visible = participants.slice(0, 5);
  const overflow = participants.length - visible.length;

  useEffect(() => {
    const createdUrls: string[] = [];
    if (participants.length === 0) return;
    participants.forEach((p) => {
      if (p.userId === currentUser?.id && currentUser.photo) {
        setPhotos(prev => ({ ...prev, [p.userId]: currentUser.photo! }));
        return;
      }
      setPhotos(prev => {
        if (p.userId in prev) return prev;
        fetchWithAuth(`/api/users/${p.userId}/photo`)
          .then(res => { if (!res.ok) throw new Error(); return res.blob(); })
          .then(blob => {
            const url = URL.createObjectURL(blob);
            createdUrls.push(url);
            setPhotos(p2 => ({ ...p2, [p.userId]: url }));
          })
          .catch(() => setPhotos(p2 => ({ ...p2, [p.userId]: null })));
        return { ...prev, [p.userId]: null };
      });
    });
    return () => createdUrls.forEach(url => URL.revokeObjectURL(url));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation.id, participants.length]);

  /**
   * Remove the group chat from the sidebar, reset the open chat view and
   * navigate home. Done synchronously *before* the network call so the UI
   * updates immediately and the mounted GroupChat (with its polling) can't
   * re-add the conversation via UPDATE_GROUP_CHAT while we wait on the request.
   */
  const resetAfterExit = () => {
    appStateContext?.dispatch({ type: 'DELETE_GROUP_CHAT', payload: conversationId! });
    clearChatTransientState(appStateContext);
    clearCustomGPT(appStateContext);
    clearAgent(appStateContext);
    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: null });
    appStateContext?.dispatch({ type: 'SET_CHATPANEL_TRANSITIONED', payload: false });
    navigate('/');
  };

  const handleLeave = () => {
    if (!conversationId || isSubmitting) return;
    setIsSubmitting(true);
    resetAfterExit();
    leaveGroupConversation(conversationId);
  };

  const handleDelete = () => {
    if (!conversationId || isSubmitting) return;
    setIsSubmitting(true);
    resetAfterExit();
    deleteGroupConversation(conversationId);
  };

  const syncParticipants = (nextParticipants: GroupParticipant[]) => {
    const nextConversation = replaceConversationParticipants(conversation, nextParticipants);
    syncGroupConversationState(appStateContext?.dispatch, currentChat, nextConversation);
  };

  const handleInvited = (invitedParticipants: GroupParticipant[]) => {
    syncParticipants(mergeGroupParticipants(participants, invitedParticipants));
  };

  const handleRemoveParticipant = async (participantUserId: string) => {
    if (!conversationId || removingParticipantUserId) return;

    setRemovingParticipantUserId(participantUserId);
    try {
      const success = await removeGroupParticipant(conversationId, participantUserId);
      if (!success) return;

      syncParticipants(participants.filter((participant) => participant.userId !== participantUserId));
    } finally {
      setRemovingParticipantUserId(null);
    }
  };

  const CircleAvatar = ({ p, index, size = 28 }: { p: GroupParticipant; index: number; size?: number }) => (
    <div
      className={`rounded-full overflow-hidden flex-shrink-0 ${!photos[p.userId] ? AVATAR_COLORS[index % AVATAR_COLORS.length] : ''}`}
      style={{ width: size, height: size }}
    >
      {photos[p.userId]
        ? <img src={photos[p.userId]!} alt={p.name} className="w-full h-full object-cover" />
        : <span className="w-full h-full flex items-center justify-center text-primary-content font-medium" style={{ fontSize: size * 0.4 }}>
            {p.name[0]?.toUpperCase()}
          </span>
      }
    </div>
  );

  return (
    <div className="flex items-center gap-2">
      {/* Avatar stack */}
      <div className="flex -space-x-2">
        {visible.map((p, i) => (
          <div key={p.userId} title={p.name}>
            <CircleAvatar p={p} index={i} />
          </div>
        ))}
        {overflow > 0 && (
          <div
            className="rounded-full bg-neutral flex items-center justify-center flex-shrink-0"
            style={{ width: 28, height: 28 }}
            title={`${overflow} more participants`}
          >
            <span className="text-neutral-content text-xs">+{overflow}</span>
          </div>
        )}
      </div>

      {/* Count — click to open participant list */}
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="flex items-center gap-1 text-sm">
            <Users size={18} />
            <span>{participants.length}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" sideOffset={8} className="bg-secondary border-transparent rounded-3xl shadow-short w-96 p-3 dark:text-white">
          <div className="mb-3 flex items-center justify-between gap-2 px-1">
            <p className="text-xs font-semibold opacity-50 uppercase tracking-wide">
              {participants.length} participant{participants.length !== 1 ? 's' : ''}
            </p>
            {isOwner && conversationId && (
              <InviteParticipantsModal
                conversationId={conversationId}
                existingParticipants={participants}
                onInvited={handleInvited}
                trigger={
                  <button type="button" className="btn btn-ghost btn-xs gap-1 rounded-full px-2">
                    <UserPlus size={14} />
                    Add People
                  </button>
                }
              />
            )}
          </div>
          <ul className="user-menu list-none p-0 m-0">
            {participants.map((p, i) => (
              <li key={p.userId} className="flex items-center gap-2.5 rounded-xl py-1">
                <CircleAvatar p={p} index={i} size={28} />
                <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-col">
                    <span className="text-sm truncate leading-tight">
                      {p.name}
                    </span>
                    {conversation.ownerId === p.userId && (
                      <span className="text-xs opacity-50">Owner</span>
                    )}
                  </div>
                  {isOwner && p.userId !== conversation.ownerId && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs text-error hover:text-error"
                      onClick={() => handleRemoveParticipant(p.userId)}
                      disabled={removingParticipantUserId === p.userId}
                      title={`Remove ${p.name}`}
                    >
                      {removingParticipantUserId === p.userId ? <span className="loading loading-spinner loading-xs" /> : <X size={14} />}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>

      {/* Leave/Delete */}
      {isOwner ? (
        <Dialog>
          <DialogTrigger asChild>
            <button
              type="button"
              className="btn btn-ghost btn-xs hover:text-error"
              disabled={isSubmitting}
              title="Delete group"
            >
              {isSubmitting ? <span className="loading loading-spinner loading-xs" /> : <Trash2 size={18} />}
            </button>
          </DialogTrigger>
          <DialogContent className="bg-secondary border-transparent rounded-3xl shadow-short">
            <DialogHeader>
              <DialogTitle className="mb-3">Delete group chat?</DialogTitle>
              <DialogDescription>Are you sure that you want to delete <strong>{conversation.title}</strong>?</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild><button type="button" className="btn btn-secondary">Cancel</button></DialogClose>
              <DialogClose asChild><button type="button" className="btn btn-error" onClick={handleDelete}>Delete</button></DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : (
        <Dialog>
          <DialogTrigger asChild>
            <button
              type="button"
              className="btn btn-ghost btn-xs hover:text-error"
              disabled={isSubmitting}
              title="Leave group"
            >
              {isSubmitting ? <span className="loading loading-spinner loading-xs" /> : <LogOut size={18} />}
            </button>
          </DialogTrigger>
          <DialogContent className="bg-secondary border-transparent rounded-3xl shadow-short">
            <DialogHeader>
              <DialogTitle className="mb-3">Leave group chat?</DialogTitle>
              <DialogDescription>Are you sure that you want to leave <strong>{conversation.title}</strong>?</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild><button type="button" className="btn btn-secondary">Cancel</button></DialogClose>
              <DialogClose asChild><button type="button" className="btn btn-error" onClick={handleLeave}>Leave</button></DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default GroupParticipants;
